// ============================================================
// api/adminTrialRequests.js
// Endpoint bindings for the mgmt/admin/trials endpoints (Trial
// Request system). Admin-tenant JWT, same client as every other
// admin binding.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-trials-list.js
//   backend/func-carehub-api/src/functions/admin-trials-detail.js
//   backend/func-carehub-api/src/functions/admin-trials-provision.js
//   backend/func-carehub-api/src/functions/admin-trials-extend.js
//   (convert: backend/func-carehub-api/src/functions/admin-bc-billing.js, since 2026-10-22)
//   backend/func-carehub-api/src/functions/admin-trials-decline.js
//   backend/func-carehub-api/src/functions/admin-trials-notes-patch.js
// ============================================================

import { apiGet, apiPost, apiPatch } from './client';

/**
 * List trial requests (newest first) with pagination + optional
 * status filter. Returns statusCounts regardless of filter.
 *
 * @param {Object} [params={}]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {('New'|'Provisioned'|'Extended'|'Converted'|'Declined')} [params.status]
 * @param {number} [params.clientId]  Reverse lookup: trials linked to one Client
 *                                    (feeds the ManualInviteBanner requester prefill)
 *
 * @returns {Promise<{
 *   items: Array<TrialRequest>,
 *   totalCount: number, page: number, pageSize: number,
 *   statusCounts: { New:number, Provisioned:number, Extended:number, Converted:number, Declined:number },
 * }>}
 */
export async function listAdminTrialRequests(params = {}) {
  return apiGet('/mgmt/admin/trials', params);
}

/** Single trial request. Throws ApiError 404 'trial_not_found' if absent. */
export async function getAdminTrialRequest(trialRequestId) {
  return apiGet(`/mgmt/admin/trials/${trialRequestId}`);
}

/** Provision: link a New request to a Tenant + Client and start the 30-day window. */
export async function provisionAdminTrialRequest(trialRequestId, { tenantId, clientId }) {
  return apiPost(`/mgmt/admin/trials/${trialRequestId}/provision`, { tenantId, clientId });
}

/** Extend: add 30 days, reset the warning, set Status=Extended. */
export async function extendAdminTrialRequest(trialRequestId) {
  return apiPost(`/mgmt/admin/trials/${trialRequestId}/extend`, {});
}

// Convert moved to api/bcBilling.js convertToPaid (2026-10-22).

/** Decline: mark Status=Declined. */
export async function declineAdminTrialRequest(trialRequestId) {
  return apiPost(`/mgmt/admin/trials/${trialRequestId}/decline`, {});
}

/** Update the Notes field only. */
export async function updateAdminTrialRequestNotes(trialRequestId, notes) {
  return apiPatch(`/mgmt/admin/trials/${trialRequestId}/notes`, { notes });
}
